// services/chatStream/parseChatStreamEvent.ts
import type {
  ChatActionDetail,
  ChatActionResolvedResult,
  ChatActionResolvedStatus,
  ChatBlockPlacement,
  ChatContextLabel,
  ChatStreamEventEnvelope,
  ChatCitation,
  ChatDailyLimits,
  ChatFollowup,
  ChatNavigationTarget,
  ChatStreamEvent,
} from '../../types/chatStream';
import { parseFactIds, parseRegions } from '../../utils/documentRegions';
import { readChatActionResult } from '../chatActionPayload';
import { parseChatBlockPosition } from '../chatBlockPosition';
import type { SseFrame } from './sseFrameParser';

/** Why a frame was rejected. Surfaced as a warning, never thrown. */
export type ChatStreamParseFailure =
  'unknown_event' | 'invalid_json' | 'missing_envelope' | 'invalid_payload';

export type ChatStreamParseResult =
  | { ok: true; event: ChatStreamEvent }
  | {
      ok: false;
      reason: ChatStreamParseFailure;
      /**
       * Present when the frame carried a valid `turnId`/`seq` but we could not
       * turn it into an event. The reader needs it to advance its seq cursor
       * past a deliberately skipped event instead of reporting a false gap.
       */
      envelope?: ChatStreamEventEnvelope;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const record = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

/**
 * The server-rendered detail rows of an action proposal (ORA-607). A row is
 * kept only when both halves are strings; a backend that sends none leaves the
 * card to humanise `args` itself, so an empty list is `undefined`, not `[]`.
 */
const parseActionDetails = (value: unknown): ChatActionDetail[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const details = value.flatMap((raw) => {
    if (!isRecord(raw)) return [];

    const label = str(raw.label);
    const detail = str(raw.value);

    return label && detail ? [{ label, value: detail }] : [];
  });

  return details.length > 0 ? details : undefined;
};

const RESOLVED_STATUSES: readonly ChatActionResolvedStatus[] = [
  'EXECUTED',
  'FAILED',
  'DISMISSED',
  'EXPIRED',
];

const isResolvedStatus = (
  value: string | null
): value is ChatActionResolvedStatus =>
  value !== null &&
  RESOLVED_STATUSES.includes(value as ChatActionResolvedStatus);

const parseLimits = (value: unknown): ChatDailyLimits | null => {
  if (!isRecord(value)) return null;

  return {
    chatsRemaining: num(value.chatsRemaining),
    chatsLimit: num(value.chatsLimit),
  };
};

const CONTEXT_KINDS = new Set<ChatContextLabel['kind']>([
  'question',
  'flashcard',
  'lecture',
  'video',
  'article',
  'exam',
]);

/** Ignore malformed optional context rather than rejecting an otherwise usable answer. */
const parseContextLabel = (value: unknown): ChatContextLabel | undefined => {
  if (!isRecord(value)) return undefined;

  const kind = str(value.kind);
  const label = str(value.label)?.trim();
  if (!kind || !CONTEXT_KINDS.has(kind as ChatContextLabel['kind']))
    return undefined;
  if (!label || label.length > 100) return undefined;

  const lectureId = str(value.lectureId);
  const blockPosition = parseChatBlockPosition(value.blockPosition);
  return {
    kind: kind as ChatContextLabel['kind'],
    label,
    ...(kind === 'lecture' && lectureId ? { lectureId } : {}),
    ...(kind === 'question' && blockPosition ? { blockPosition } : {}),
  };
};

const parseTarget = (value: unknown): ChatNavigationTarget | null => {
  if (!isRecord(value)) return null;

  const route = str(value.route);
  if (!route) return null;

  const params = isRecord(value.params)
    ? Object.fromEntries(
        Object.entries(value.params).flatMap(([key, param]) => {
          const parsed = str(param);
          return parsed === null ? [] : [[key, parsed] as const];
        })
      )
    : undefined;

  return params ? { route, params } : { route };
};

/**
 * The `result` an `action.resolved` carries (ORA-750) — the action's own
 * outcome, in the shape the confirm endpoint returns it, read as defensively as
 * `chatActions.ts` reads it there: an unrecognised `kind`, or a result missing
 * the one field that makes it useful, becomes null rather than reaching the card.
 *
 * `support_sent` (ORA-743) says the message is already with Intercom;
 * `open_support` is the older shape a backend without the send still sends.
 */
const parseActionResult = (value: unknown): ChatActionResolvedResult | null => {
  return readChatActionResult(value);
};

const parseCitations = (value: unknown): ChatCitation[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((raw) => {
    if (!isRecord(raw)) return [];

    const id = str(raw.id);
    const kind = str(raw.kind);
    // Own-data citations (ORA-514) name no content row and carry an empty
    // `contentId`; only a missing or non-string one is malformed.
    const contentId = str(raw.contentId);
    if (!id || !kind || contentId === null) return [];

    const citation: ChatCitation = { id, kind, contentId };
    const title = str(raw.title);
    if (title !== null) citation.title = title;
    if (raw.sectionId !== undefined) citation.sectionId = str(raw.sectionId);
    if (raw.page !== undefined) citation.page = num(raw.page);
    const lectureId = str(raw.lectureId);
    if (lectureId !== null) citation.lectureId = lectureId;
    const documentId = str(raw.documentId);
    if (documentId !== null) citation.documentId = documentId;
    const excerpt = str(raw.excerpt);
    if (excerpt !== null) citation.excerpt = excerpt;
    const label = str(raw.label);
    if (label !== null) citation.label = label;
    if (typeof raw.rendered === 'boolean') citation.rendered = raw.rendered;
    if (typeof raw.addable === 'boolean') citation.addable = raw.addable;
    // Only the document kinds the side viewer (ORA-736) reads carry their
    // target off the stream; every other kind resolves as it always has.
    if (kind === 'document_page' || kind === 'chat_document_page') {
      const target = parseTarget(raw.target);
      if (target) citation.target = target;
    }
    // A lecture page citation into its document model (Document Pipeline
    // v4) carries the facts it cites and their boxes, for the viewer's
    // highlights. Malformed entries are dropped; none leaves the old shape.
    if (kind === 'document_page') {
      const factIds = parseFactIds(raw.factIds);
      if (factIds.length > 0) citation.factIds = factIds;
      const regions = parseRegions(raw.regions);
      if (regions.length > 0) citation.regions = regions;
    }

    return [citation];
  });
};

const parseFollowups = (value: unknown): ChatFollowup[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap<ChatFollowup>((raw) => {
    if (!isRecord(raw)) return [];

    const id = str(raw.id);
    const label = str(raw.label);
    if (!id || !label) return [];

    if (raw.kind === 'navigate') {
      const target = parseTarget(raw.target);
      return target ? [{ id, kind: 'navigate', label, target }] : [];
    }

    const prompt = str(raw.prompt);
    if (!prompt) return [];

    return [
      {
        id,
        kind: 'prompt',
        label,
        prompt,
        // An optional, additive discriminator (ORA-827); an unknown value is
        // dropped and the chip is drawn as an ordinary prompt.
        ...(raw.intent === 'study' ? { intent: 'study' as const } : {}),
      },
    ];
  });
};

const parsePlacement = (value: unknown): ChatBlockPlacement =>
  value === 'inline' ? 'inline' : 'end';

const invalid = (
  reason: ChatStreamParseFailure = 'invalid_payload'
): ChatStreamParseResult => ({ ok: false, reason });

/**
 * A well-formed frame this client version cannot render — a value added to an
 * enum it knows, say. Reported like an unknown event NAME rather than as
 * corruption, so the reader skips it and still advances its seq cursor instead
 * of reporting a hole in the stream (ORA-750).
 */
const skippable = (
  envelope: ChatStreamEventEnvelope
): ChatStreamParseResult => ({
  ok: false,
  reason: 'unknown_event',
  envelope,
});

/**
 * Turn one SSE frame into a typed ora-be ADR-0006 event.
 *
 * Every event must carry `turnId` and a numeric `seq`; a frame that does not is
 * rejected rather than guessed at, because the slice's ordering rules depend on
 * both. Unknown event names are rejected too — a future protocol version adds
 * events, and this client is expected to ignore what it cannot render.
 */
export const parseChatStreamEvent = (
  frame: SseFrame
): ChatStreamParseResult => {
  let data: unknown;

  try {
    data = JSON.parse(frame.data);
  } catch {
    return invalid('invalid_json');
  }

  if (!isRecord(data)) return invalid('invalid_json');

  const turnId = str(data.turnId);
  const seq = num(data.seq);
  if (!turnId || seq === null) return invalid('missing_envelope');

  const base = { turnId, seq };

  switch (frame.event) {
    case 'turn.start': {
      const conversationId = str(data.conversationId);
      if (!conversationId) return invalid();

      return {
        ok: true,
        event: {
          ...base,
          type: 'turn.start',
          protocol: num(data.protocol) ?? 0,
          conversationId,
          model: str(data.model) ?? 'unknown',
          limits: parseLimits(data.limits),
        },
      };
    }

    case 'status': {
      const label = str(data.label);
      if (label === null) return invalid();

      return { ok: true, event: { ...base, type: 'status', label } };
    }

    case 'tool.call': {
      const callId = str(data.callId);
      const name = str(data.name);
      if (!callId || !name) return invalid();

      return {
        ok: true,
        event: {
          ...base,
          type: 'tool.call',
          callId,
          name,
          args: record(data.args),
        },
      };
    }

    case 'tool.result': {
      const callId = str(data.callId);
      if (!callId || typeof data.ok !== 'boolean') return invalid();

      return {
        ok: true,
        event: {
          ...base,
          type: 'tool.result',
          callId,
          ok: data.ok,
          ms: num(data.ms) ?? 0,
          summary: str(data.summary) ?? '',
        },
      };
    }

    case 'block': {
      const blockId = str(data.blockId);
      const kind = str(data.kind);
      if (!blockId || !kind) return invalid();

      return {
        ok: true,
        event: {
          ...base,
          type: 'block',
          blockId,
          kind,
          placement: parsePlacement(data.placement),
          payload: record(data.payload),
          target: parseTarget(data.target),
        },
      };
    }

    case 'text.delta': {
      const delta = str(data.delta);
      if (delta === null) return invalid();

      return { ok: true, event: { ...base, type: 'text.delta', delta } };
    }

    case 'text.done': {
      const text = str(data.text);
      if (text === null) return invalid();

      const contextLabel = parseContextLabel(data.contextLabel);

      return {
        ok: true,
        event: {
          ...base,
          type: 'text.done',
          text,
          citations: parseCitations(data.citations),
          ...(contextLabel ? { contextLabel } : {}),
        },
      };
    }

    case 'followups':
      return {
        ok: true,
        event: {
          ...base,
          type: 'followups',
          items: parseFollowups(data.items),
        },
      };

    case 'action.proposed': {
      const actionId = str(data.actionId);
      const name = str(data.name);
      if (!actionId || !name) return invalid();

      return {
        ok: true,
        event: {
          ...base,
          type: 'action.proposed',
          actionId,
          name,
          args: record(data.args),
          summary: str(data.summary) ?? '',
          details: parseActionDetails(data.details),
          confirmLabel: str(data.confirmLabel) ?? undefined,
        },
      };
    }

    case 'action.resolved': {
      const actionId = str(data.actionId);
      const name = str(data.name);
      const status = str(data.status);

      if (!actionId || !name) return invalid();

      // A status from a newer runtime: the frame is fine, this client just has
      // no behaviour for it. Skip it without faking a gap.
      if (!isResolvedStatus(status)) return skippable(base);

      // `proposedTurnId`, not `turnId`: the envelope owns that name and carries
      // the turn being streamed, not the one that proposed the card.
      const proposedTurnId = str(data.proposedTurnId);
      const result = parseActionResult(data.result);
      const target = parseTarget(data.target);
      const summary = str(data.summary);
      const error = str(data.error);

      return {
        ok: true,
        event: {
          ...base,
          type: 'action.resolved',
          actionId,
          ...(proposedTurnId !== null ? { proposedTurnId } : {}),
          name,
          status,
          ...(result ? { result } : {}),
          ...(target ? { target } : {}),
          ...(summary !== null ? { summary } : {}),
          ...(error !== null ? { error } : {}),
        },
      };
    }

    case 'title': {
      const title = str(data.title);
      if (title === null) return invalid();

      return { ok: true, event: { ...base, type: 'title', title } };
    }

    case 'turn.done':
      return {
        ok: true,
        event: {
          ...base,
          type: 'turn.done',
          traceId: str(data.traceId) ?? '',
          steps: num(data.steps) ?? 0,
          ms: num(data.ms) ?? 0,
          limits: parseLimits(data.limits),
          messageId: str(data.messageId),
        },
      };

    case 'error': {
      const code = str(data.code);
      if (!code) return invalid();

      const partial = str(data.partial);
      const message = str(data.message);
      // Present only when the failed turn still persisted its partial answer
      // (ORA-459); the slice reads both to decide whether the conversation
      // survived the failure.
      const messageId = str(data.messageId);
      const conversationId = str(data.conversationId);

      return {
        ok: true,
        event: {
          ...base,
          type: 'error',
          code,
          retryable: data.retryable === true,
          ...(message !== null ? { message } : {}),
          ...(partial !== null ? { partial } : {}),
          ...(messageId !== null ? { messageId } : {}),
          ...(conversationId !== null ? { conversationId } : {}),
        },
      };
    }

    case 'ping':
      return { ok: true, event: { ...base, type: 'ping' } };

    default:
      // Adding an event is additive under ora-be ADR-0006, so an unrecognised name is
      // a newer server, not corruption. Hand back the envelope so the reader
      // can skip it without treating the next event as a gap.
      return { ok: false, reason: 'unknown_event', envelope: base };
  }
};
